import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Brain, AlertTriangle, TrendingUp, TrendingDown, Award, Target, Zap, CheckCircle, XCircle, Calendar, Activity, Clock } from 'lucide-react';
import UpgradePrompt from './components/UpgradePrompt';
import { hasActiveSubscription, getUserSubscription } from './subscriptionUtils';
import { getTrades } from './services/firestoreService';
import { buildSmartPrompt } from './utils/smartPrompt';
import { auth } from './firebase';
import app from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ReactMarkdown from 'react-markdown';
import './AIInsights.css';

const AIInsights = ({ currentUser, onNavigate }) => {
  const [trades, setTrades] = useState([]);
  const [insights, setInsights] = useState({
    mistakes: [],
    patterns: [],
    recommendations: [],
    performance: {}
  });
  const [isPaid, setIsPaid] = useState(null); // null = loading
  const [isElite, setIsElite] = useState(false);
  const [smartReport, setSmartReport] = useState(null);
  const [smartReportLoading, setSmartReportLoading] = useState(false);

  const generateSmartReport = async (tradeData) => {
    if (smartReportLoading || smartReport || tradeData.length < 3) return;
    setSmartReportLoading(true);
    try {
      const groqFn = httpsCallable(getFunctions(app), 'groqChat');
      const userName = auth.currentUser?.displayName || 'Trader';
      const prompt = buildSmartPrompt(tradeData, userName);
      if (!prompt) return;
      const result = await groqFn({ messages: [{ role: 'user', content: prompt }] });
      if (result.data?.reply) setSmartReport(result.data.reply);
    } catch (err) {
      console.error('[SmartReport] Error:', err);
    } finally {
      setSmartReportLoading(false);
    }
  };

  // AI Mistake Detection Algorithm — defined BEFORE useEffect so the effect closure can call it
  const analyzeTrades = (tradesData) => {
    const mistakes = [];
    const patterns = [];
    const recommendations = [];

    // Calculate basic stats
    const totalTrades = tradesData.length;
    const winningTrades = tradesData.filter(t => t.pnl > 0);
    const losingTrades = tradesData.filter(t => t.pnl < 0);
    const totalPnL = tradesData.reduce((sum, t) => sum + t.pnl, 0);
    const winRate = (winningTrades.length / totalTrades) * 100;

    const avgWin = winningTrades.length > 0
      ? winningTrades.reduce((sum, t) => sum + t.pnl, 0) / winningTrades.length
      : 0;

    const avgLoss = losingTrades.length > 0
      ? losingTrades.reduce((sum, t) => sum + t.pnl, 0) / losingTrades.length
      : 0;

    const riskReward = avgLoss !== 0 ? Math.abs(avgWin / avgLoss) : 0;

    // MISTAKE 1: Low Win Rate
    if (winRate < 50) {
      mistakes.push({
        id: 'low-winrate',
        severity: 'critical',
        title: 'Low Win Rate',
        description: `आपका win rate ${winRate.toFixed(1)}% है जो 50% से कम है।`,
        impact: 'High',
        trades_affected: totalTrades,
        loss_amount: Math.abs(totalPnL),
        solution: 'Entry strategy improve करें और trade selection पर focus करें।'
      });

      recommendations.push({
        id: 'improve-winrate',
        priority: 'high',
        title: 'Win Rate Improve करें',
        action: 'केवल high probability setups में trade करें। Technical analysis और market trends study करें।',
        expected_impact: '+15-20% win rate improvement संभव है'
      });
    }

    // MISTAKE 2: Poor Risk:Reward
    if (riskReward < 1.5) {
      mistakes.push({
        id: 'poor-rr',
        severity: 'high',
        title: 'Poor Risk:Reward Ratio',
        description: `आपका risk:reward ratio 1:${riskReward.toFixed(2)} है जो optimal (1:2+) से कम है।`,
        impact: 'High',
        trades_affected: totalTrades,
        loss_amount: 0,
        solution: 'Target को बड़ा रखें और stop loss को tight करें।'
      });

      recommendations.push({
        id: 'improve-rr',
        priority: 'high',
        title: 'Risk:Reward Optimize करें',
        action: 'हर trade में minimum 1:2 risk:reward maintain करें। Small losses, big wins का mantra follow करें।',
        expected_impact: 'Profitability में 30-40% improvement'
      });
    }

    // MISTAKE 3: Overtrading Detection
    const tradesPerDay = {};
    tradesData.forEach(trade => {
      const date = trade.date;
      tradesPerDay[date] = (tradesPerDay[date] || 0) + 1;
    });

    const maxTradesPerDay = Math.max(...Object.values(tradesPerDay));
    const avgTradesPerDay = Object.values(tradesPerDay).reduce((a, b) => a + b, 0) / Object.keys(tradesPerDay).length;

    if (avgTradesPerDay > 5 || maxTradesPerDay > 8) {
      const overtradingDays = Object.entries(tradesPerDay).filter(([_, count]) => count > 5).length;

      mistakes.push({
        id: 'overtrading',
        severity: 'high',
        title: 'Overtrading Detected',
        description: `Average ${avgTradesPerDay.toFixed(1)} trades/day है। Quality over quantity principle follow नहीं हो रहा।`,
        impact: 'High',
        trades_affected: overtradingDays * avgTradesPerDay,
        loss_amount: 0,
        solution: 'Daily trade limit set करें (max 3-4 quality trades)।'
      });

      recommendations.push({
        id: 'reduce-trading',
        priority: 'critical',
        title: 'Trading Frequency Reduce करें',
        action: 'Quality over quantity! दिन में maximum 3-4 high-confidence trades लें। Patience practice करें।',
        expected_impact: 'Win rate में 10-15% improvement'
      });
    }

    // MISTAKE 4: Consecutive Losses Pattern (Revenge Trading)
    let consecutiveLosses = 0;
    let maxConsecutiveLosses = 0;
    let revengeTradesPossible = 0;

    tradesData.sort((a, b) => new Date(a.date) - new Date(b.date)).forEach((trade, idx) => {
      if (trade.pnl < 0) {
        consecutiveLosses++;
        maxConsecutiveLosses = Math.max(maxConsecutiveLosses, consecutiveLosses);

        // Check if next trade is also a loss (possible revenge trade)
        if (idx < tradesData.length - 1 && tradesData[idx + 1].pnl < 0) {
          revengeTradesPossible++;
        }
      } else {
        consecutiveLosses = 0;
      }
    });

    if (maxConsecutiveLosses >= 3 || revengeTradesPossible > 2) {
      mistakes.push({
        id: 'revenge-trading',
        severity: 'critical',
        title: 'Revenge Trading Pattern',
        description: `${maxConsecutiveLosses} consecutive losses detected। Loss के बाद emotional trading हो रही है।`,
        impact: 'Very High',
        trades_affected: revengeTradesPossible,
        loss_amount: Math.abs(losingTrades.slice(0, revengeTradesPossible).reduce((sum, t) => sum + t.pnl, 0)),
        solution: 'Loss के बाद mandatory 30 min break लें। Emotional state check करें।'
      });

      recommendations.push({
        id: 'stop-revenge',
        priority: 'critical',
        title: 'Revenge Trading रोकें',
        action: 'Rule बनाएं: 2 consecutive losses के बाद trading stop करें। Next day fresh start करें।',
        expected_impact: '₹' + Math.abs(avgLoss * revengeTradesPossible).toFixed(0) + ' monthly savings'
      });
    }

    // PATTERN 1: Stock Performance Analysis
    const stockPerformance = {};
    tradesData.forEach(trade => {
      if (!stockPerformance[trade.stock]) {
        stockPerformance[trade.stock] = { trades: 0, profit: 0, loss: 0, pnl: 0 };
      }
      stockPerformance[trade.stock].trades++;
      stockPerformance[trade.stock].pnl += trade.pnl;
      if (trade.pnl > 0) {
        stockPerformance[trade.stock].profit++;
      } else {
        stockPerformance[trade.stock].loss++;
      }
    });

    const bestStock = Object.entries(stockPerformance)
      .sort((a, b) => b[1].pnl - a[1].pnl)[0];

    const worstStock = Object.entries(stockPerformance)
      .sort((a, b) => a[1].pnl - b[1].pnl)[0];

    if (bestStock) {
      patterns.push({
        id: 'best-stock',
        type: 'positive',
        title: 'Best Performing Stock',
        description: `${bestStock[0]} में सबसे अच्छा performance (${((bestStock[1].profit / bestStock[1].trades) * 100).toFixed(0)}% win rate)`,
        action: `${bestStock[0]} में और opportunities देखें`,
        confidence: 85
      });
    }

    if (worstStock && worstStock[1].pnl < 0) {
      patterns.push({
        id: 'worst-stock',
        type: 'negative',
        title: 'Worst Performing Stock',
        description: `${worstStock[0]} में consistently loss (₹${Math.abs(worstStock[1].pnl).toFixed(0)} total loss)`,
        action: `${worstStock[0]} में trading avoid करें या strategy change करें`,
        confidence: 90
      });
    }

    // PATTERN 2: Time-based Analysis
    const dayWisePerformance = {};
    tradesData.forEach(trade => {
      const dayOfWeek = new Date(trade.date).toLocaleDateString('en-US', { weekday: 'long' });
      if (!dayWisePerformance[dayOfWeek]) {
        dayWisePerformance[dayOfWeek] = { trades: 0, wins: 0, pnl: 0 };
      }
      dayWisePerformance[dayOfWeek].trades++;
      dayWisePerformance[dayOfWeek].pnl += trade.pnl;
      if (trade.pnl > 0) dayWisePerformance[dayOfWeek].wins++;
    });

    const bestDay = Object.entries(dayWisePerformance)
      .sort((a, b) => (b[1].wins / b[1].trades) - (a[1].wins / a[1].trades))[0];

    const worstDay = Object.entries(dayWisePerformance)
      .sort((a, b) => (a[1].wins / a[1].trades) - (b[1].wins / b[1].trades))[0];

    if (bestDay) {
      patterns.push({
        id: 'best-day',
        type: 'positive',
        title: 'Best Trading Day',
        description: `${bestDay[0]} को ${((bestDay[1].wins / bestDay[1].trades) * 100).toFixed(0)}% win rate`,
        action: `${bestDay[0]} को ज्यादा focus करें`,
        confidence: 78
      });
    }

    if (worstDay && (worstDay[1].wins / worstDay[1].trades) < 0.4) {
      patterns.push({
        id: 'worst-day',
        type: 'negative',
        title: 'Worst Trading Day',
        description: `${worstDay[0]} को only ${((worstDay[1].wins / worstDay[1].trades) * 100).toFixed(0)}% win rate`,
        action: `${worstDay[0]} को trading avoid करें या extra cautious रहें`,
        confidence: 82
      });
    }

    // General Recommendations
    if (totalPnL < 0) {
      recommendations.push({
        id: 'overall-loss',
        priority: 'critical',
        title: 'Overall Loss Reduction Strategy',
        action: 'सभी mistakes fix करने पर focus करें। Small position sizes से शुरुआत करें। Paper trading consider करें।',
        expected_impact: `Total loss ₹${Math.abs(totalPnL).toFixed(0)} को recover करने का potential`
      });
    }

    setInsights({
      mistakes,
      patterns,
      recommendations,
      performance: {
        totalTrades,
        winRate,
        avgWin,
        avgLoss,
        riskReward,
        totalPnL
      }
    });
  };

  // Load user's trades and subscription status from Firestore
  useEffect(() => {
    // Clear stale data immediately on any user change
    setTrades([]);
    setInsights({ mistakes: [], patterns: [], recommendations: [], performance: {} });
    setIsPaid(null);
    setIsElite(false);

    if (currentUser) {
      console.log('[AIInsights] Loading for user:', currentUser.id);
      const load = async () => {
        const [paid, sub] = await Promise.all([
          hasActiveSubscription(currentUser.id),
          getUserSubscription(currentUser.id),
        ]);
        setIsPaid(paid);
        setIsElite(paid && (sub?.planId === 'elite' || sub?.planId === 'premium'));
        if (paid) {
          const userTrades = await getTrades(currentUser.id);
          console.log('[AIInsights] Fetched', userTrades.length, 'trades for user:', currentUser.id);
          setTrades(userTrades);
          if (userTrades.length > 0) {
            analyzeTrades(userTrades);
            generateSmartReport(userTrades);
          }
        }
      };
      load();
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gate: loading state
  if (isPaid === null) return null;

  // Gate: require active subscription
  if (!isPaid) {
    return (
      <UpgradePrompt
        requiredPlan="pro"
        feature="AI Insights Pro plan mein available hai. Apni trading mistakes aur patterns discover karo."
      />
    );
  }

  // Prepare chart data
  const prepareChartData = () => {
    const sortedTrades = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
    let cumulativePnL = 0;

    return sortedTrades.map(trade => {
      cumulativePnL += trade.pnl;
      return {
        date: new Date(trade.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        pnl: trade.pnl,
        cumulative: cumulativePnL,
        stock: trade.stock
      };
    });
  };

  const chartData = trades.length > 0 ? prepareChartData() : [];

  // Mistake severity colors
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return { borderLeft: '4px solid var(--danger)', background: 'var(--danger-bg)' };
      case 'high': return { borderLeft: '4px solid var(--warning)', background: 'var(--warning-bg)' };
      case 'medium': return { borderLeft: '4px solid var(--warning)', background: 'var(--warning-bg)' };
      default: return { borderLeft: '4px solid var(--accent)', background: 'var(--accent-subtle)' };
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <XCircle style={{ color: 'var(--danger)' }} size={24} />;
      case 'high': return <AlertTriangle style={{ color: 'var(--warning)' }} size={24} />;
      default: return <AlertTriangle style={{ color: 'var(--warning)' }} size={24} />;
    }
  };

  if (trades.length === 0) {
    return (
      <div className="ai-card ai-empty">
        <Brain size={64} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
        <h3 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>No Trades to Analyze</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>कम से कम 5 trades add करें AI insights के लिए</p>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>जितने ज्यादे trades, उतना बेहतर analysis!</p>
      </div>
    );
  }

  return (
    <div className="ai-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* AI Analysis Header */}
      <div className="ai-card" style={{ marginBottom: 0 }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div style={{ width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent-subtle)' }}>
              <Brain size={22} style={{ color: 'var(--accent)' }} />
            </div>
            <div>
              <h2 className="ai-page-title" style={{ marginBottom: 2 }}>AI Trading Insights</h2>
              <p className="ai-page-subtitle" style={{ marginBottom: 0 }}>Powered by Advanced Analytics Engine</p>
            </div>
          </div>
          <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 16px' }}>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>Analyzing</p>
            <p style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{trades.length} Trades</p>
          </div>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="ai-stat-grid" style={{ marginBottom: 0 }}>
        <div className="ai-stat" style={{ borderLeft: '3px solid var(--accent)' }}>
          <p className="ai-stat-label">Win Rate</p>
          <p className="ai-stat-value" style={{ color: 'var(--accent)' }}>
            {insights.performance.winRate?.toFixed(1)}%
          </p>
        </div>
        <div className="ai-stat" style={{ borderLeft: '3px solid var(--profit)' }}>
          <p className="ai-stat-label">Avg Win</p>
          <p className="ai-stat-value" style={{ color: 'var(--profit)' }}>
            ₹{insights.performance.avgWin?.toFixed(0)}
          </p>
        </div>
        <div className="ai-stat" style={{ borderLeft: '3px solid var(--loss)' }}>
          <p className="ai-stat-label">Avg Loss</p>
          <p className="ai-stat-value" style={{ color: 'var(--loss)' }}>
            ₹{Math.abs(insights.performance.avgLoss || 0).toFixed(0)}
          </p>
        </div>
        <div className="ai-stat" style={{ borderLeft: '3px solid var(--accent)' }}>
          <p className="ai-stat-label">Risk:Reward</p>
          <p className="ai-stat-value" style={{ color: 'var(--accent)' }}>
            1:{insights.performance.riskReward?.toFixed(2)}
          </p>
        </div>
      </div>

      {/* P&L Over Time Chart */}
      <div className="ai-card" style={{ marginBottom: 0 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <TrendingUp style={{ color: 'var(--accent)' }} />
          <span>P&L Performance Over Time</span>
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="cumulative"
              stroke="#8b5cf6"
              name="Cumulative P&L (₹)"
              strokeWidth={3}
            />
            <Line
              type="monotone"
              dataKey="pnl"
              stroke="#3b82f6"
              name="Trade P&L (₹)"
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Mistakes Detected */}
      <div className="ai-card" style={{ marginBottom: 0 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle style={{ color: 'var(--danger)' }} />
          <span>Mistakes Detected ({insights.mistakes.length})</span>
        </h3>

        {insights.mistakes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <CheckCircle size={48} style={{ margin: '0 auto 12px', color: 'var(--profit)' }} />
            <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>No Major Mistakes Detected!</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Great job! Keep maintaining this discipline.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {insights.mistakes.map((mistake) => (
              <div
                key={mistake.id}
                style={{ borderRadius: 10, padding: 16, ...getSeverityColor(mistake.severity) }}
              >
                <div className="flex items-start space-x-3">
                  {getSeverityIcon(mistake.severity)}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>{mistake.title}</h4>
                      <span style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                        {mistake.severity.toUpperCase()}
                      </span>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>{mistake.description}</p>
                    <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Impact: </span>
                        <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{mistake.impact}</span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Trades Affected: </span>
                        <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{mistake.trades_affected}</span>
                      </div>
                    </div>
                    <div style={{ background: 'var(--bg-primary)', borderRadius: 8, padding: 12 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>💡 Solution:</p>
                      <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{mistake.solution}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Recommendations */}
      <div className="ai-card" style={{ marginBottom: 0 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Zap style={{ color: 'var(--warning)' }} />
          <span>AI Recommendations</span>
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {insights.recommendations.map((rec) => (
            <div
              key={rec.id}
              style={{
                padding: 16,
                borderRadius: 10,
                borderLeft: `4px solid ${rec.priority === 'critical' ? 'var(--danger)' : rec.priority === 'high' ? 'var(--warning)' : 'var(--accent)'}`,
                background: rec.priority === 'critical' ? 'var(--danger-bg)' : rec.priority === 'high' ? 'var(--warning-bg)' : 'var(--accent-subtle)'
              }}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{rec.title}</h4>
                <span style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                  {rec.priority.toUpperCase()}
                </span>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 6 }}>{rec.action}</p>
              <p style={{ color: 'var(--profit)', fontSize: 13, fontWeight: 600 }}>
                Expected Impact: {rec.expected_impact}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Patterns Recognized */}
      <div className="ai-card" style={{ marginBottom: 0 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity style={{ color: 'var(--profit)' }} />
          <span>Patterns Recognized</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insights.patterns.map((pattern) => (
            <div
              key={pattern.id}
              style={{
                padding: 16,
                borderRadius: 10,
                background: pattern.type === 'positive' ? 'var(--success-bg)' : 'var(--danger-bg)',
                border: `1px solid ${pattern.type === 'positive' ? 'var(--profit)' : 'var(--danger)'}`
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{pattern.title}</h4>
                {pattern.type === 'positive' ? (
                  <TrendingUp style={{ color: 'var(--profit)' }} size={20} />
                ) : (
                  <TrendingDown style={{ color: 'var(--loss)' }} size={20} />
                )}
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>{pattern.description}</p>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                Action: {pattern.action}
              </p>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{pattern.confidence}% confidence</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action Plan */}
      <div className="ai-card" style={{ marginBottom: 0 }}>
        <h3 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Target size={28} style={{ color: 'var(--accent)' }} />
          <span>Your Action Plan</span>
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ padding: 16, borderRadius: 10, background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
            <p style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>Step 1: Fix Critical Mistakes</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              सबसे पहले critical mistakes (revenge trading, overtrading) fix करें
            </p>
          </div>
          <div style={{ padding: 16, borderRadius: 10, background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
            <p style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>Step 2: Follow AI Recommendations</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              High priority recommendations implement करें step by step
            </p>
          </div>
          <div style={{ padding: 16, borderRadius: 10, background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
            <p style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>Step 3: Track Progress</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Weekly analysis करें कि improvement हो रही है या नहीं
            </p>
          </div>
        </div>
      </div>

      {/* Smart AI Personalized Report — Elite only */}
      <div className="ai-card" style={{ marginBottom: 0 }}>
        <div className="flex items-center space-x-3 mb-4">
          <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent-subtle)' }}>
            <Zap size={18} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h3 style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)', margin: 0 }}>Smart AI Coach Report</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>Personalized advice based on your patterns</p>
          </div>
        </div>

        {!isElite ? (
          <UpgradePrompt
            requiredPlan="elite"
            feature="Smart AI Coach Report Elite plan mein available hai. Monthly personalized coaching report pao."
          />
        ) : (
          <>
            {smartReportLoading && (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ width: 32, height: 32, border: '3px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Analyzing your trading patterns...</p>
              </div>
            )}

            {smartReport && !smartReportLoading && (
              <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-primary)' }}>
                <ReactMarkdown>{smartReport}</ReactMarkdown>
              </div>
            )}

            {!smartReport && !smartReportLoading && trades.length >= 3 && (
              <button
                onClick={() => generateSmartReport(trades)}
                style={{ width: '100%', padding: '10px', background: 'var(--accent)', color: '#000', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
              >
                Generate Smart AI Report →
              </button>
            )}

            {!smartReport && !smartReportLoading && trades.length < 3 && (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
                Kam se kam 3 trades chahiye Smart AI Report ke liye.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AIInsights;
